import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getTokenPrefix } from './helper';
import { getCookie, setCookie } from '../cookie-utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import axios from 'axios';
import { getEnv } from '../env';

const helperAxios = axios.create();

export async function configureAmplify(): Promise<any> {
  arrangeCognitoIdentityStorage();

  const environment = getTokenPrefix();

  const urlObj = new URL(window.location.href);
  const authCodeParam = urlObj.searchParams.get('authCode');
  const integrationUrlLocale = urlObj.searchParams.get('locale') || '';

  if (authCodeParam) {
    try {
      const response = await helperAxios.post(`${getEnv().VITE_APP_OWL_THIRD_PARTY_URL}/tokens`, {
        authCode: authCodeParam
      });

      const userPoolId = response.data.CognitoPoolId;
      const appClientId = response.data.AppClientId;
      const idToken = response.data.IdToken;
      const accessToken = response.data.AccessToken;
      const refreshToken = response.data.RefreshToken;
      const username = JSON.parse(atob(idToken.split('.')[1])).sub;

      // Persist in cookies if you still want them
      setCookie(`${environment}-userPoolID`, userPoolId);
      setCookie(`${environment}-userPoolClientID`, appClientId);
      setCookie(`${environment}-locale`, integrationUrlLocale);

      const storageKeyPrefix = `CognitoIdentityServiceProvider.${appClientId}`;
      sessionStorage.setItem(`${storageKeyPrefix}.${username}.idToken`, idToken);
      sessionStorage.setItem(`${storageKeyPrefix}.${username}.accessToken`, accessToken);
      sessionStorage.setItem(`${storageKeyPrefix}.${username}.refreshToken`, refreshToken);
      sessionStorage.setItem(`${storageKeyPrefix}.LastAuthUser`, username);
    } catch (err) {
      console.error('Failed to exchange authCode', err);
    }
  }

  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolClientId: getCookie(`${environment}-userPoolClientID`) || '',
          userPoolId: getCookie(`${environment}-userPoolID`) || ''
        }
      }
    });
    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage as any);
    await fetchAuthSession();
  } catch {
    return null;
  }
}

export async function getIdToken(): Promise<string | null> {
  try {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

export async function getUserRoles(): Promise<string[]> {
  try {
    const { tokens } = await fetchAuthSession();
    const claim = tokens?.idToken?.payload?.['onera:roles'];
    if (typeof claim !== 'string') return [];
    const parsed: { role: string }[] = JSON.parse(claim);
    return parsed.map(({ role }) => role);
  } catch {
    return [];
  }
}

export function getOrigin(): string | null {
  const lastAuthUserKey = Object.keys(sessionStorage).find((k) => k.endsWith('.LastAuthUser'));

  if (!lastAuthUserKey) return null;

  const prefix = lastAuthUserKey.replace('.LastAuthUser', '');
  const username = sessionStorage.getItem(lastAuthUserKey);

  if (!username) return null;

  const token = sessionStorage.getItem(`${prefix}.${username}.idToken`);

  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const origin = payload['custom:originV2'];

    return typeof origin === 'string' ? origin : null;
  } catch {
    return null;
  }
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const { tokens } = await fetchAuthSession();

    return tokens?.accessToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Transfer OWL CORE cookies from document.cookie to sessionStorage and delete originals
 */
function arrangeCognitoIdentityStorage(): void {
  document.cookie
    .split('; ')
    .map((c) => c.split('=')[0].trim())
    .filter((key) => key.startsWith('CognitoIdentityServiceProvider'))
    .forEach((key) => {
      const value = document.cookie
        .split('; ')
        .find((c) => c.startsWith(key + '='))
        ?.split('=')[1];

      if (value !== undefined) {
        sessionStorage.setItem(key, decodeURIComponent(value));
        document.cookie = `${key}=; path=/; domain=.myonera.com; Secure; SameSite=Strict; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    });
}

/**
 * Set session storage cognito values to cookie for OWL Core
 */
export function restoreCognitoIdentityToCookies(): void {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith('CognitoIdentityServiceProvider')) {
      const value = sessionStorage.getItem(key);

      if (value) {
        const cookieValue = encodeURIComponent(value);
        const domain = '.myonera.com';

        document.cookie = `${key}=${cookieValue}; path=/; domain=${domain}; Secure; SameSite=Strict`;
      }
    }
  });
}
